import { supabase } from './supabase';
import type { Post, Contact, SiteSettings, PostCategory } from './types';

// ============================================================
// Posts (public)
// ============================================================
export async function fetchPublishedPosts(category?: string): Promise<Post[]> {
  let query = supabase
    .from('posts')
    .select('*')
    .eq('published', true)
    .order('created_at', { ascending: false });
  if (category && category !== 'all') {
    query = query.eq('category', category);
  }
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return data as Post[];
}

export async function fetchPostBySlug(slug: string): Promise<Post | null> {
  const { data, error } = await supabase
    .from('posts')
    .select('*')
    .eq('slug', slug)
    .eq('published', true)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data as Post | null;
}

// ============================================================
// Posts (admin)
// ============================================================
export async function adminFetchPosts(): Promise<Post[]> {
  const { data, error } = await supabase
    .from('posts')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return data as Post[];
}

export async function adminCreatePost(data: Partial<Post>): Promise<Post> {
  const { data: { session } } = await supabase.auth.getSession();
  const payload = {
    title: data.title || '',
    slug: data.slug || slugify(data.title || ''),
    excerpt: data.excerpt || '',
    content: data.content || '',
    category: (data.category || 'news') as PostCategory,
    cover_image_url: data.cover_image_url || '',
    published: data.published ?? false,
    author_id: session?.user?.id ?? null,
  };
  const { data: row, error } = await supabase
    .from('posts')
    .insert(payload)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return row as Post;
}

export async function adminUpdatePost(id: string, data: Partial<Post>): Promise<Post> {
  const updates: Record<string, unknown> = { ...data, updated_at: new Date().toISOString() };
  if (data.title && !data.slug) updates.slug = slugify(data.title);
  delete updates.id;
  const { data: row, error } = await supabase
    .from('posts')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return row as Post;
}

export async function adminDeletePost(id: string): Promise<void> {
  const { error } = await supabase.from('posts').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ============================================================
// Contacts (admin)
// ============================================================
export async function adminFetchContacts(): Promise<Contact[]> {
  const { data, error } = await supabase
    .from('contacts')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return data as Contact[];
}

export async function adminUpdateContactRead(id: string, read: boolean): Promise<void> {
  const { error } = await supabase.from('contacts').update({ read }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function adminDeleteContact(id: string): Promise<void> {
  const { error } = await supabase.from('contacts').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ============================================================
// Public contact submission
// ============================================================
export async function submitContact(data: {
  name: string;
  email: string;
  subject: string;
  message: string;
}): Promise<void> {
  const { error } = await supabase.from('contacts').insert({
    name: data.name.trim(),
    email: data.email.trim(),
    subject: data.subject.trim(),
    message: data.message.trim(),
  });
  if (error) throw new Error(error.message);
}

// ============================================================
// Settings
// ============================================================
export async function fetchSettings(): Promise<SiteSettings> {
  const { data, error } = await supabase.from('site_settings').select('key, value');
  if (error) throw new Error(error.message);
  const settings: SiteSettings = {};
  (data || []).forEach((row: { key: string; value: string }) => {
    settings[row.key] = row.value;
  });
  return settings;
}

export async function adminUpdateSettings(settings: SiteSettings): Promise<void> {
  const updates = Object.entries(settings).map(([key, value]) => ({
    key,
    value: String(value),
    updated_at: new Date().toISOString(),
  }));
  const { error } = await supabase
    .from('site_settings')
    .upsert(updates, { onConflict: 'key' });
  if (error) throw new Error(error.message);
}

// ============================================================
// Stats (admin)
// ============================================================
export async function adminFetchStats(): Promise<{
  totalPosts: number;
  publishedPosts: number;
  draftPosts: number;
  totalContacts: number;
  unreadContacts: number;
  postsByCategory: { category: string; count: number }[];
  postsByMonth: { month: string; count: number }[];
}> {
  const { data: posts, error: postsErr } = await supabase
    .from('posts')
    .select('id, category, published, created_at');
  if (postsErr) throw new Error(postsErr.message);

  const { data: contacts, error: contactsErr } = await supabase
    .from('contacts')
    .select('id, read');
  if (contactsErr) throw new Error(contactsErr.message);

  const totalPosts = posts.length;
  const publishedPosts = posts.filter((p: { published: boolean }) => p.published).length;
  const draftPosts = totalPosts - publishedPosts;
  const totalContacts = contacts.length;
  const unreadContacts = contacts.filter((c: { read: boolean }) => !c.read).length;

  const categoryMap: Record<string, number> = {};
  posts.forEach((p: { category: string }) => {
    categoryMap[p.category] = (categoryMap[p.category] || 0) + 1;
  });
  const postsByCategory = Object.entries(categoryMap).map(([category, count]) => ({ category, count }));

  const monthMap: Record<string, number> = {};
  posts.forEach((p: { created_at: string }) => {
    const d = new Date(p.created_at);
    const key = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    monthMap[key] = (monthMap[key] || 0) + 1;
  });
  const postsByMonth = Object.entries(monthMap)
    .map(([month, count]) => ({ month, count }))
    .slice(-6);

  return { totalPosts, publishedPosts, draftPosts, totalContacts, unreadContacts, postsByCategory, postsByMonth };
}

function slugify(text: string): string {
  return text.toLowerCase().trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
